import { useMemo } from 'react';
import { EntityInstance } from '../types';
import { AlertEntity } from '@investigator/shared/generated/graphql';
import { IQueryAlertMetadataExcludedEntitiesPaginated } from '../../detections/queries';

interface UseEntitySuppressionStatusParams {
  /** The currently selected entity from context (may be different from sourceEntity) */
  selectedEntity?: EntityInstance;
  /** The source entity from the current detection */
  sourceEntity?: AlertEntity | null;
  /** Query data for excluded entities */
  excludedEntitiesData?: IQueryAlertMetadataExcludedEntitiesPaginated;
}

/**
 * Determines the suppression status of an entity by checking context first,
 * then falling back to query data.
 *
 * This hook provides a unified way to check entity suppression status across
 * the app, ensuring that:
 * 1. After mutations, the button updates immediately using context data
 * 2. On initial load, it uses the query data
 * 3. The status is always accurate and responsive to user actions
 *
 * @param selectedEntity - The currently selected entity from context
 * @param sourceEntity - The source entity from the detection
 * @param excludedEntitiesData - The query result for excluded entities
 * @returns boolean indicating if the entity is suppressed
 *
 * @example
 * ```typescript
 * const isSuppressed = useEntitySuppressionStatus({
 *   selectedEntity,
 *   sourceEntity,
 *   excludedEntitiesData,
 * });
 * ```
 */
export const useEntitySuppressionStatus = ({
  selectedEntity,
  sourceEntity,
  excludedEntitiesData,
}: UseEntitySuppressionStatusParams): boolean => {
  return useMemo(() => {
    // Check if the selected entity in context matches the current detection's source entity
    const isMatchingEntity =
      selectedEntity &&
      sourceEntity &&
      selectedEntity.entityId === sourceEntity.entity_id &&
      selectedEntity.entityName === sourceEntity.entity_name;

    // If we have a matching entity in context with a defined suppression status,
    // use it (this will be updated immediately after suppress/unsuppress mutations)
    if (isMatchingEntity && selectedEntity.entityIsSuppressed !== undefined) {
      return selectedEntity.entityIsSuppressed;
    }

    // Otherwise, fall back to the query data (used on initial load)
    return (
      (excludedEntitiesData?.queryAlertMetadataExcludedEntitiesPaginated
        ?.excluded_entities?.length ?? 0) > 0
    );
  }, [selectedEntity, sourceEntity, excludedEntitiesData]);
};
